import { zodResolver } from '@hookform/resolvers/zod';
import {
  type OutletResource,
  OutletsController_list_v1,
  type OutletStockResource,
  type StockLevelResource,
  StockLevelsController_read_v1,
} from '@repo/api-client';
import { Alert, AlertDescription, AlertTitle } from '@repo/ui/components/alert';
import { Badge } from '@repo/ui/components/badge';
import { Button } from '@repo/ui/components/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@repo/ui/components/form';
import { Input } from '@repo/ui/components/input';
import { Label } from '@repo/ui/components/label';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@repo/ui/components/select';
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from '@repo/ui/components/table';
import { IconAlertTriangle, IconClockSearch, IconTruckDelivery } from '@tabler/icons-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router';
import { z } from 'zod';

import { useApiClient } from '@/api/api-client';
import { collectPages } from '@/api/collect-pages';
import { isNotFound } from '@/api/failure';
import { useSession } from '@/auth/session';
import { formatInstant } from '@/lib/instant';
import { isoInstantFrom, localInstantSchema } from '@/lib/local-instant';

import { fieldLabel, fieldSurface } from '../auth/AuthFrame';
import {
  EmptyState,
  LoadFailed,
  Screen,
  ScreenFact,
  ScreenTable,
  TableNotice,
  TableSkeleton,
} from '../back-office/Screen';
import { isRetired, labelForKind, labelForStock } from '../outlets/outlet-kinds';
import { paths } from '../paths';
import { countBelowZero, formatLevel, isBelowZero } from './stock-levels';

/**
 * What an outlet holds: `GET /v1/outlets/{outletId}/stock`.
 *
 * A row per item the outlet stocks, with the level the records say is on the
 * shelf. `stock-levels.ts` beside this file owns the two renderings that lose
 * information if they are got wrong — zero, and a negative — and says why at
 * length; nothing here restates them.
 *
 * ## The screen is a whole read, and that is the choice rather than the default
 *
 * `docs/ui-conventions.md`, "Reading a list", asks that the strategy be picked.
 * There is nothing to pick here and that is worth stating: **this route is not
 * paged**. It declares no `limit` or `offset` and answers `OutletStockResource`
 * — one `asOf` and every item — because a position is one consistent snapshot at
 * one instant, and a page of it would be half a snapshot. So there is no `Pager`
 * under the table and adding paging back would be inventing it.
 *
 * The set is bounded by what one outlet stocks, which is bounded by the park.
 *
 * ## Why the outlet is a filter rather than a path parameter
 *
 * `/outlets/:id/catalogue` exists and this could have matched it. It does not,
 * for a reason that is about who reads this screen: `GET /v1/outlets/{id}/stock`
 * is `@RequireRole()` — **any signed-in user** — so the route is
 * `requireSignedIn()`, and a screen reachable only from a link on an
 * owner-and-manager screen would be a screen nobody else could ever open. It
 * therefore has a navigation entry of its own, and a navigation entry needs a
 * path with no id in it.
 *
 * The outlet is then a filter in the rail, exactly as "Sold at" is on the
 * catalogue, and it is filled from `GET /v1/outlets` — which is scoped to the
 * caller's own outlets by the same rule (FR-111), so the picker offers what the
 * reader may actually read.
 *
 * ## 404 is "no such outlet", in those words and no others
 *
 * An outlet outside the caller's reach answers exactly as an unknown one does
 * (FR-128), and the API means that: the two are indistinguishable on purpose,
 * and a screen that guessed which it was and said "you are not allowed to see
 * this outlet" would re-open the enumeration the server closed. So there is one
 * message, and it is about the outlet rather than about the reader.
 *
 * It is reachable even though the picker only offers outlets the caller can
 * read: an outlet retired between the picker loading and a row being asked for
 * is gone from this route's answer, and a URL is a thing people keep open.
 */

/** Every state the levels can be in. One value, so two cannot coexist. */
type LevelsState =
  | { readonly kind: 'idle' }
  | { readonly kind: 'loading' }
  | { readonly kind: 'loaded'; readonly stock: OutletStockResource }
  | { readonly kind: 'failed'; readonly error: unknown };

/** Kept next to the header cells below, so the two cannot drift. */
const COLUMNS = 3;

/** The moment field, which is the whole of this screen's one form. */
const asOfSchema = z.object({ at: localInstantSchema });

export function OutletStock() {
  const client = useApiClient();
  const { session } = useSession();

  /**
   * Whether to offer the way to record a delivery (FE037).
   *
   * **Courtesy and not security**, exactly as the catalogue's own write controls
   * are: the role came out of `sessionStorage` and is editable by whoever is at
   * the keyboard, the route it leads to declares `requireRole('OWNER',
   * 'MANAGER')` of its own, and the API refuses regardless. What hiding buys is
   * that somebody who cannot record one is not shown a button that answers them
   * with a refusal they can do nothing about — this screen is deliberately open
   * to every signed-in user, so that is a real case here rather than a
   * hypothetical one.
   */
  const mayRecordMovements = session?.user.role === 'OWNER' || session?.user.role === 'MANAGER';

  const [outlets, setOutlets] = useState<readonly OutletResource[]>([]);
  const [outletId, setOutletId] = useState<string | null>(null);

  // Whether the outlets have answered *at all*, which is not the same question
  // as whether there are any. Without it the notice under an empty picker would
  // read "no outlet is in reach from this account" for as long as the first
  // request takes — a sentence about somebody's assignments, said before
  // anything is known about them.
  const [outletsAnswered, setOutletsAnswered] = useState(false);

  // The instant asked about, as the API takes it, or `null` for "now" — which is
  // the same computation with the server's own clock and is what an omitted
  // `asOf` means (R-508). Held as the ISO string rather than as what was typed
  // so that what the request carries is what was validated.
  const [asOf, setAsOf] = useState<string | null>(null);

  const [state, setState] = useState<LevelsState>({ kind: 'idle' });

  // A counter rather than a boolean, so two retries in a row are two distinct
  // values and the second one still fires.
  const [reloads, setReloads] = useState(0);
  const reload = useCallback(() => setReloads((count) => count + 1), []);

  const form = useForm({
    resolver: zodResolver(asOfSchema),
    mode: 'onTouched',
    defaultValues: { at: '' },
  });

  useEffect(() => {
    const controller = new AbortController();
    let live = true;

    // Read whole: the outlets are bounded by the park, which is the case
    // `docs/ui-conventions.md` gives `collectPages` for. Retired outlets are
    // excluded by the API's own default (FR-112) and this does not ask for them
    // — a retired outlet trades nothing, so there is no shelf to count.
    collectPages<OutletResource>((offset, limit) =>
      client.request(OutletsController_list_v1, {
        query: { offset, limit },
        signal: controller.signal,
      }),
    )
      .then((all) => {
        if (!live) return;

        setOutlets(all);
        setOutletsAnswered(true);

        // One outlet in reach is the common case for anybody who is not an owner
        // or a manager, and asking them to pick from a list of one is a control
        // that cannot do anything. Never overrides a choice already made.
        setOutletId((chosen) => chosen ?? (all.length === 1 ? (all[0]?.id ?? null) : null));
      })
      .catch(() => {
        // The picker is the only casualty and the screen says so where the
        // picker is, rather than replacing the whole page with an error about a
        // control. A reader who arrived with an outlet already chosen keeps it.
        //
        // Not `outletsAnswered`: a failed read has answered nothing about who is
        // assigned where, so the notice keeps the wording that is true either
        // way rather than telling somebody they staff no outlets because a
        // request timed out.
        if (live) setOutlets([]);
      });

    return () => {
      live = false;
      controller.abort();
    };
  }, [client, reloads]);

  useEffect(() => {
    if (outletId === null) {
      setState({ kind: 'idle' });

      return;
    }

    // Two guards, not one. `controller` stops the request itself the moment the
    // outlet or the moment changes, and `live` stops an answer that was already
    // in flight from landing after this effect was torn down — without it,
    // switching outlets twice quickly can leave the second outlet's rows
    // underneath the first's.
    const controller = new AbortController();
    let live = true;

    setState({ kind: 'loading' });

    client
      .request(StockLevelsController_read_v1, {
        path: { outletId },

        // Sent only when a moment was asked for. An absent `asOf` is the
        // server's current instant, which is not the same as this device's idea
        // of now — and the query string is what appears in a log when somebody
        // is working out what a screen asked for.
        query: asOf === null ? {} : { asOf },
        signal: controller.signal,
      })
      .then((stock) => {
        if (live) setState({ kind: 'loaded', stock });
      })
      .catch((error: unknown) => {
        if (live) setState({ kind: 'failed', error });
      });

    return () => {
      live = false;
      controller.abort();
    };
  }, [client, outletId, asOf, reloads]);

  // Base UI resolves what the trigger shows from `items` and not from the
  // rendered options, so a picker without it shows a bare UUID where an outlet
  // name belongs. Derived from the same rows the options render, so the two
  // cannot drift.
  const outletItems = useMemo(
    () => outlets.map((outlet) => ({ label: outlet.name, value: outlet.id })),
    [outlets],
  );

  const outlet = outlets.find((candidate) => candidate.id === outletId);

  const missing = state.kind === 'failed' && isNotFound(state.error);

  const items: readonly StockLevelResource[] = state.kind === 'loaded' ? state.stock.items : [];
  const belowZero = countBelowZero(items);

  const applyAsOf = (values: z.infer<typeof asOfSchema>) => {
    const instant = isoInstantFrom(values.at);

    // The resolver has already refused everything this can be null for. Read as
    // an impossible state rather than asserted away with a `!`: a change to the
    // schema that let a malformed value through would show as a field that
    // refuses rather than as a request carrying `undefined`.
    if (instant === null) {
      form.setError('at', { message: 'That is not a real date and time.' });

      return;
    }

    setAsOf(instant);
  };

  return (
    <Screen
      title="What this outlet holds"
      action={
        mayRecordMovements ? (
          // The screen's one accent, and the reason it now has one: a level that
          // is wrong is looked at here and put right elsewhere, so the act that
          // changes it belongs where the figure is. `Screen` puts it in the rail
          // beside the sentence saying what the screen is for.
          <Button
            // The rendered element is an anchor, so it is not a native button
            // and says so — without this Base UI warns that button semantics
            // have been removed, and a link announced as a button is a link
            // that does not answer the space bar.
            nativeButton={false}
            render={<Link to={paths.stockDeliveries} />}
          >
            <IconTruckDelivery data-icon="inline-start" />
            Record a delivery
          </Button>
        ) : undefined
      }
      aside={
        <>
          <div className="space-y-2">
            <Label htmlFor="outlet-picker" className="text-sm text-muted-foreground">
              Outlet
            </Label>
            <Select
              value={outletId}
              items={outletItems}
              onValueChange={(next) => {
                setOutletId(next);
              }}
            >
              <SelectTrigger
                id="outlet-picker"
                aria-label="Outlet"
                // Nothing to choose between until the outlets land, and a picker
                // with no options is a control that cannot do anything.
                disabled={outlets.length === 0}
                className="h-10 w-full bg-card"
              >
                <SelectValue placeholder="Choose an outlet" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {outlets.map((option) => (
                    <SelectItem key={option.id} value={option.id}>
                      {option.name}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>

          {outlet !== undefined && (
            <ScreenFact label="Outlet">
              <span className="font-mono numeric">{outlet.code}</span> — {outlet.name}
              {isRetired(outlet) && (
                <>
                  {' '}
                  {/* `outline` and not `destructive`: retiring an outlet is an
                      ordinary end to an ordinary life. */}
                  <Badge variant="outline">Retired</Badge>
                </>
              )}
              <br />
              <span className="text-muted-foreground">
                {labelForKind(outlet.kind)}, {labelForStock(outlet.carriesStock).toLowerCase()}
              </span>
            </ScreenFact>
          )}

          {state.kind === 'loaded' && (
            <>
              <ScreenFact label="Items stocked">
                <span className="font-mono numeric">{items.length}</span>{' '}
                {items.length === 1 ? 'item' : 'items'}
              </ScreenFact>

              {/* Only when there are any. A count that reads "0 below zero" on
                  every ordinary day is a line everybody stops reading, and this
                  one has to be noticed on the day it is not zero. */}
              {belowZero > 0 && (
                <ScreenFact label="Below zero">
                  <span className="font-mono numeric">{belowZero}</span>{' '}
                  {belowZero === 1 ? 'item' : 'items'}
                  <br />
                  <span className="text-muted-foreground">A stocktake settles it.</span>
                </ScreenFact>
              )}

              {/* The instant the **server** answered for, echoed from the
                  response rather than from what was typed. They are the same
                  when a moment was asked for; when none was, this is the only
                  place the position's instant exists at all — and it is the
                  server's clock, which is the one that decides what a level is
                  (R-508). */}
              <ScreenFact label="Position as at">
                <span className="font-mono numeric">{formatInstant(state.stock.asOf)}</span>
              </ScreenFact>
            </>
          )}

          <Form {...form}>
            <form
              className="space-y-3"
              noValidate
              onSubmit={(event) => void form.handleSubmit(applyAsOf)(event)}
            >
              <FormField
                control={form.control}
                name="at"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel required className={fieldLabel}>
                      Held at
                    </FormLabel>
                    <FormControl
                      render={
                        <Input
                          // A native date-and-time field rather than a box asking
                          // a manager to write ISO 8601, and the same control
                          // `PriceAsOf` asks the same question with. It is typed
                          // in the zone the answer is rendered in, so a moment
                          // can be read off a row and typed straight back in
                          // without anybody converting to UTC in their head.
                          type="datetime-local"
                          className={`${fieldSurface} h-11 w-full numeric`}
                          required
                          {...field}
                        />
                      }
                    />
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="flex flex-wrap gap-2">
                {/* `outline`, not the accent. Looking a moment up is not this
                    screen's primary action — recording a delivery is, and
                    `docs/design-language.md` allows exactly one accent, which
                    that button in the rail is now spending. */}
                <Button
                  type="submit"
                  variant="outline"
                  className="h-10"
                  // Nothing to look up until there is an outlet to look it up
                  // for. The field stays usable so a moment can be typed first.
                  disabled={outletId === null}
                >
                  <IconClockSearch data-icon="inline-start" />
                  Look up
                </Button>

                {asOf !== null && (
                  <Button
                    type="button"
                    variant="ghost"
                    className="h-10"
                    onClick={() => {
                      setAsOf(null);
                      form.reset({ at: '' });
                    }}
                  >
                    Back to now
                  </Button>
                )}
              </div>
            </form>
          </Form>
        </>
      }
    >
      {missing ? (
        <OutletMissing />
      ) : (
        <ScreenTable label="What this outlet holds">
          <TableHeader>
            <TableRow>
              <TableHead className="w-40">Code</TableHead>
              <TableHead>Item</TableHead>
              {/* Quantities right-align, per docs/ui-conventions.md — comparing
                  down a column is the task. */}
              <TableHead className="w-52 text-right">Level</TableHead>
            </TableRow>
          </TableHeader>

          {state.kind === 'loading' && <TableSkeleton columns={COLUMNS} />}

          {state.kind === 'idle' && (
            <TableNotice columns={COLUMNS}>
              <EmptyState
                title="Choose an outlet"
                description={
                  outletsAnswered && outlets.length === 0
                    ? 'This account is not posted to any outlet.'
                    : 'Its stock appears here.'
                }
              />
            </TableNotice>
          )}

          {state.kind === 'failed' && (
            <TableNotice columns={COLUMNS}>
              <LoadFailed error={state.error} onRetry={reload} />
            </TableNotice>
          )}

          {state.kind === 'loaded' &&
            (items.length === 0 ? (
              <TableNotice columns={COLUMNS}>
                <EmptyState
                  title={
                    outlet?.carriesStock === false
                      ? 'This outlet sells no goods'
                      : 'Nothing stocked'
                  }
                  description={
                    outlet?.carriesStock === false
                      ? 'There is nothing here to count.'
                      : 'No item that counts stock is sold here yet.'
                  }
                />
              </TableNotice>
            ) : (
              <TableBody>
                {/* In the order the API sent them, which is by code. Nothing
                    here sorts, filters or adds a row — see `stock-levels.ts`. */}
                {items.map((item) => (
                  <LevelRow key={item.catalogueItemId} item={item} />
                ))}
              </TableBody>
            ))}
        </ScreenTable>
      )}
    </Screen>
  );
}

/**
 * One item and what the records say is on the shelf.
 *
 * No control of any kind: this screen reads. A delivery, a correction and a
 * stocktake are the three acts that change a level (CL-502, CL-503) and each is
 * its own ticket.
 */
function LevelRow({ item }: { item: StockLevelResource }) {
  const short = isBelowZero(item.level);

  return (
    <TableRow>
      {/* A code is read aloud and typed back in, so it gets the mono face and
          tabular figures. */}
      <TableCell className="font-mono text-foreground numeric">{item.code}</TableCell>

      <TableCell className="whitespace-normal">{item.name}</TableCell>

      <TableCell className="text-right">
        <span className="inline-flex items-center justify-end gap-2">
          {short && (
            // `outline` and not `destructive`. Red in this interface means the
            // system refused something or is about to (docs/ui-conventions.md,
            // Colour); a negative level is neither — it is a true figure that
            // needs somebody to go and count. The badge is what makes it
            // impossible to read the row as an ordinary one.
            <Badge variant="outline">
              {/* `data-icon` is the registry's own hook for the tighter padding
                  a badge with a leading icon wants — badge.tsx keys its
                  `pl-1.5` off it. Sized by the badge, not here. */}
              <IconAlertTriangle data-icon="inline-start" aria-hidden />
              Below zero
            </Badge>
          )}
          <span className="font-mono text-foreground numeric">{formatLevel(item.level)}</span>
        </span>
      </TableCell>
    </TableRow>
  );
}

/**
 * There is no such outlet — or it is one this caller cannot reach.
 *
 * The two are one answer by the API's design (FR-128) and this says the one
 * thing that is true of both. Deliberately **not** worded as a permissions
 * refusal: guessing which it was would tell somebody probing for outlets that a
 * particular id exists, which is exactly the enumeration the shared 404 closes.
 */
function OutletMissing() {
  return (
    <Alert>
      <IconAlertTriangle />
      <AlertTitle>There is no such outlet</AlertTitle>
      <AlertDescription>It may have been retired. Pick an outlet from the list.</AlertDescription>
    </Alert>
  );
}
